import React from "react";
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Copy, Check, Clock, Link } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const InvitesPage = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);
  const [duration, setDuration] = React.useState<number>(24);

  // Fetch user's invites and invite limit
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('invite_limit, is_admin')
        .eq('id', user?.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: invites, isLoading } = useQuery({
    queryKey: ['invites', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('invites')
        .select('*, profiles!invites_redeemed_by_fkey(username)')
        .eq('creator_id', user?.id)
        .order('created_at', { ascending: false });
      return data;
    },
    enabled: !!user?.id,
  });

  // Create new regular invite mutation
  const createInviteMutation = useMutation({
    mutationFn: async () => {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const { data, error } = await supabase
        .from('invites')
        .insert({
          creator_id: user?.id,
          code,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      toast({
        title: "Invite Created",
        description: "Your invite code has been generated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create invite. You may have reached your limit.",
        variant: "destructive",
      });
    },
  });

  // Create new timed invite mutation
  const createTimedInviteMutation = useMutation({
    mutationFn: async () => {
      const code = Math.random().toString(36).substring(2, 10).toUpperCase();
      const { data, error } = await supabase
        .from('invites')
        .insert({
          creator_id: user?.id,
          code,
          is_timed: true,
          duration_hours: duration,
          max_uses: null, // null means unlimited uses
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      toast({
        title: "Timed Invite Created",
        description: `Your timed invite link will be valid for ${duration} hours.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create timed invite.",
        variant: "destructive",
      });
    },
  });

  const handleCopyCode = async (code: string) => {
    const inviteUrl = `${window.location.origin}/invite/${code}`;
    await navigator.clipboard.writeText(inviteUrl);
    setCopiedCode(code);
    toast({
      title: "Copied!",
      description: "Invite link copied to clipboard.",
    });
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const remainingInvites = (profile?.invite_limit || 0) - (invites?.length || 0);

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold mb-2">Invite Management</h1>
          <p className="text-gray-400">
            {isAdmin 
              ? "As an admin, you have unlimited invites"
              : `You have ${remainingInvites} invite${remainingInvites !== 1 ? 's' : ''} remaining`
            }
          </p>
        </div>
        <div className="flex gap-4">
          <Button
            onClick={() => createInviteMutation.mutate()}
            disabled={remainingInvites <= 0 && !isAdmin}
          >
            Generate New Invite
          </Button>
          {isAdmin && (
            <div className="flex items-end gap-2">
              <div>
                <Label htmlFor="duration" className="text-sm">Duration (hours)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className="w-24"
                />
              </div>
              <Button
                onClick={() => createTimedInviteMutation.mutate()}
                variant="secondary"
              >
                <Clock className="w-4 h-4 mr-2" />
                Create Timed Invite
              </Button>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : invites?.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          You haven't created any invites yet
        </div>
      ) : (
        <div className="space-y-4">
          {invites?.map((invite) => (
            <div
              key={invite.id}
              className="bg-black/20 backdrop-blur-sm p-4 rounded-lg flex items-center justify-between"
            >
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {invite.is_timed ? (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-400" />
                      <span className="text-blue-400">Timed Invite</span>
                      <span className="text-gray-400">({invite.duration_hours}h)</span>
                    </div>
                  ) : (
                    <span className="font-mono text-lg">{invite.code}</span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopyCode(invite.code)}
                    className={cn(
                      "h-8 w-8",
                      copiedCode === invite.code && "text-green-500"
                    )}
                  >
                    {copiedCode === invite.code ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Link className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="text-sm text-gray-400">
                  {invite.is_redeemed ? (
                    <>
                      Redeemed by{" "}
                      <span className="text-primary">
                        {invite.profiles?.username}
                      </span>
                    </>
                  ) : invite.is_timed ? (
                    "Active - Multiple uses allowed"
                  ) : (
                    "Not yet redeemed"
                  )}
                </div>
              </div>
              <div className="text-sm text-gray-400">
                {new Date(invite.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvitesPage;